import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Archive, Download, Upload, Trash2, RefreshCw, AlertTriangle, Clock, Package, HardDrive } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (b) => {
  if (!b) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  while (b >= 1024 && i < units.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(1)} ${units[i]}`;
};

const formatDate = (iso) => iso ? new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

export default function Backups() {
  const [snapshots, setSnapshots] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [snaps, sum] = await Promise.all([
        axios.get(`${API_URL}/api/backups`, { headers }),
        axios.get(`${API_URL}/api/backups/summary`, { headers }),
      ]);
      setSnapshots(snaps.data);
      setSummary(sum.data);
    } catch {
      toast.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleExportNow = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/backups/export`, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      a.download = `jsm-export-${ts}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    } finally { setBusy(false); }
  };

  const handleCreateSnapshot = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/backups/snapshot`, {}, { headers });
      toast.success('Snapshot saved!');
      fetchAll();
    } catch {
      toast.error('Snapshot failed');
    } finally { setBusy(false); }
  };

  const handleDownload = async (snap) => {
    try {
      const res = await axios.get(`${API_URL}/api/backups/${snap.id}/download`, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = snap.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (snap) => {
    if (!window.confirm(`Delete backup "${snap.filename}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/backups/${snap.id}`, { headers });
      toast.success('Backup deleted');
      fetchAll();
    } catch {
      toast.error('Delete failed');
    }
  };

  const openRestore = (snap) => { setRestoreTarget(snap); setRestoreConfirm(''); };

  const confirmRestore = async () => {
    if (restoreConfirm !== 'CONFIRM') {
      toast.error('Type CONFIRM exactly to proceed');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/backups/${restoreTarget.id}/restore`, {}, { headers });
      toast.success(`Restored ${res.data.total_docs} documents`);
      setRestoreTarget(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally { setBusy(false); }
  };

  const handleUploadRestore = async () => {
    if (!uploadFile) { toast.error('Choose a .zip first'); return; }
    if (restoreConfirm !== 'CONFIRM') { toast.error('Type CONFIRM to proceed'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const res = await axios.post(`${API_URL}/api/backups/upload-restore`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Restored ${res.data.total_docs} documents from upload`);
      setShowUploadDialog(false);
      setUploadFile(null);
      setRestoreConfirm('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload restore failed');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-64">Loading backups...</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Archive className="h-6 w-6 text-emerald-500" /> Backups & Data Recovery
            </h1>
            <p className="text-slate-500 text-sm">Export, import, and restore your entire database. Auto backup runs every 24 hours.</p>
          </div>
          <Button onClick={fetchAll} variant="outline" size="sm"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <div className="text-xs text-slate-500 uppercase mb-1">Total Backups</div>
            <div className="text-2xl font-bold">{summary?.total ?? 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-slate-500 uppercase mb-1">Manual</div>
            <div className="text-2xl font-bold text-indigo-600">{summary?.manual ?? 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-slate-500 uppercase mb-1">Auto (daily, last {summary?.auto_retention})</div>
            <div className="text-2xl font-bold text-emerald-600">{summary?.auto ?? 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-slate-500 uppercase mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Last Backup</div>
            <div className="text-sm font-semibold">{summary?.last ? formatDate(summary.last.created_at) : 'None yet'}</div>
          </CardContent></Card>
        </div>

        {/* Action buttons */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Package className="h-5 w-5" /> Quick Actions</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button onClick={handleExportNow} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-everything-btn">
              <Download className="h-4 w-4 mr-1" /> Export Everything (Download ZIP)
            </Button>
            <Button onClick={handleCreateSnapshot} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-snapshot-btn">
              <HardDrive className="h-4 w-4 mr-1" /> Create Snapshot (Save Server-Side)
            </Button>
            <Button onClick={() => setShowUploadDialog(true)} disabled={busy} variant="destructive" data-testid="upload-restore-btn">
              <Upload className="h-4 w-4 mr-1" /> Upload & Restore from ZIP
            </Button>
          </CardContent>
        </Card>

        {/* Snapshots table */}
        <Card>
          <CardHeader><CardTitle className="text-base">Saved Backups ({snapshots.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {snapshots.length === 0 ? (
              <div className="p-10 text-center text-slate-400">
                <Archive className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>No backups yet. Click "Create Snapshot" to make your first one.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Created</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Collections</TableHead>
                      <TableHead>Documents</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {snapshots.map(s => (
                      <TableRow key={s.id} data-testid={`backup-row-${s.id}`}>
                        <TableCell className="text-sm">{formatDate(s.created_at)}</TableCell>
                        <TableCell>
                          {s.backup_type === 'auto'
                            ? <Badge className="bg-emerald-600">Auto</Badge>
                            : <Badge className="bg-indigo-600">Manual</Badge>}
                        </TableCell>
                        <TableCell>{s.collection_count}</TableCell>
                        <TableCell>{(s.total_docs || 0).toLocaleString()}</TableCell>
                        <TableCell>{formatBytes(s.size_bytes)}</TableCell>
                        <TableCell className="text-xs text-slate-500">{s.created_by}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={() => handleDownload(s)} title="Download">
                              <Download className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openRestore(s)} title="Restore" className="border-amber-500 text-amber-700 hover:bg-amber-50">
                              <RefreshCw className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(s)} title="Delete">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Restore confirmation dialog */}
        <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> Restore Backup
              </DialogTitle>
              <DialogDescription>
                This will <strong>WIPE</strong> all current data and replace it with the snapshot below. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {restoreTarget && (
              <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm space-y-1">
                <div><strong>File:</strong> {restoreTarget.filename}</div>
                <div><strong>Created:</strong> {formatDate(restoreTarget.created_at)}</div>
                <div><strong>Docs:</strong> {restoreTarget.total_docs?.toLocaleString()} across {restoreTarget.collection_count} collections</div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Type <strong>CONFIRM</strong> to proceed:</Label>
              <Input value={restoreConfirm} onChange={e => setRestoreConfirm(e.target.value)} placeholder="CONFIRM" data-testid="restore-confirm-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRestoreTarget(null)}>Cancel</Button>
              <Button variant="destructive" disabled={busy || restoreConfirm !== 'CONFIRM'} onClick={confirmRestore} data-testid="restore-confirm-btn">
                {busy ? 'Restoring...' : 'Yes, Wipe & Restore'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload & Restore dialog */}
        <Dialog open={showUploadDialog} onOpenChange={(o) => { setShowUploadDialog(o); if (!o) { setUploadFile(null); setRestoreConfirm(''); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> Upload & Restore from ZIP
              </DialogTitle>
              <DialogDescription>
                Upload a previously-exported backup ZIP. This will <strong>WIPE</strong> all current data.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Backup ZIP file</Label>
                <Input type="file" accept=".zip" onChange={e => setUploadFile(e.target.files[0])} data-testid="upload-file-input" />
                {uploadFile && <p className="text-xs text-slate-500 mt-1">{uploadFile.name} ({formatBytes(uploadFile.size)})</p>}
              </div>
              <div>
                <Label>Type <strong>CONFIRM</strong> to proceed:</Label>
                <Input value={restoreConfirm} onChange={e => setRestoreConfirm(e.target.value)} placeholder="CONFIRM" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
              <Button variant="destructive" disabled={busy || !uploadFile || restoreConfirm !== 'CONFIRM'} onClick={handleUploadRestore}>
                {busy ? 'Restoring...' : 'Upload & Restore'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
